import type { FlightTrackPoint } from '@/lib/api/types';
import { type MouseEvent, useMemo } from 'react';

/** Split the track into runs of consecutive points that carry an AGL value.
 *
 *  One polyline per run, rather than one across the whole track: a point
 *  with a null `alt_agl` is a hole in the data, and bridging it with a
 *  straight segment would draw an altitude the aircraft never reported. */
function profileSegments(track: FlightTrackPoint[]): string[] {
  // A reduce, not Math.max(...spread) — a real track is ~10k points and
  // spreading that many arguments is a stack-overflow risk.
  let maxAlt = 0;
  for (const p of track) {
    if (p.alt_agl != null && p.alt_agl > maxAlt) maxAlt = p.alt_agl;
  }
  if (maxAlt <= 0) maxAlt = 1;

  const segments: string[] = [];
  let run: string[] = [];
  const lastIndex = track.length - 1;

  for (const [i, p] of track.entries()) {
    if (p.alt_agl == null) {
      if (run.length > 1) segments.push(run.join(' '));
      run = [];
      continue;
    }
    const x = ((i / lastIndex) * 100).toFixed(1);
    const y = (58 - (p.alt_agl / maxAlt) * 54).toFixed(1);
    run.push(`${x},${y}`);
  }
  if (run.length > 1) segments.push(run.join(' '));

  return segments;
}

/** AGL profile across the track, with a cursor at the scrub index.
 *  `viewBox` is a fixed 100×60 with `preserveAspectRatio="none"`, so the
 *  polyline stretches to any width; `vector-effect` keeps the stroke honest
 *  under that non-uniform scale. */
export function AltitudeProfile({
  track,
  scrubIndex,
  onScrub,
}: {
  track: FlightTrackPoint[];
  scrubIndex: number;
  onScrub: (i: number) => void;
}) {
  // Memoised on the track alone. Dragging the scrubber re-renders this on
  // every frame, and rebuilding a 10k-point path string each time is the
  // difference between a smooth drag and a stuttering one — only the cursor
  // line below actually depends on `scrubIndex`.
  const segments = useMemo(() => (track.length < 2 ? [] : profileSegments(track)), [track]);

  if (segments.length === 0) return null;

  const cursorX = (scrubIndex / (track.length - 1)) * 100;

  const seek = (e: MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    onScrub(Math.round(frac * (track.length - 1)));
  };

  return (
    // Mouse-only convenience: clicking the profile seeks the cursor. The
    // range input directly beneath is the keyboard-accessible control for
    // the same state and already carries an aria-label, so this element is
    // hidden from assistive tech rather than made a second focus stop that
    // does the same job. Suppressing rather than adding a redundant handler.
    // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard access is provided by the sibling range input; this is a redundant pointer affordance and is aria-hidden.
    <svg
      aria-hidden="true"
      width="100%"
      height="60"
      viewBox="0 0 100 60"
      preserveAspectRatio="none"
      onClick={seek}
      className="w-full h-[60px] block cursor-pointer"
    >
      <title>Altitude profile (AGL)</title>
      {segments.map((points) => (
        <polyline
          key={points.slice(0, 32)}
          points={points}
          fill="none"
          stroke="var(--color-green)"
          strokeWidth="1.5"
          vectorEffect="non-scaling-stroke"
        />
      ))}
      <line
        x1={cursorX}
        y1={0}
        x2={cursorX}
        y2={60}
        stroke="var(--color-red)"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
